import { formatLocationText } from "@/components/AlgoliaSearch/utils";
import { sectionConsultantRoleLabel } from "../types";
import type {
  CareerConsultantAssignment,
  CoverageGroupBy,
  CoverageGroupMember,
  CoverageGroupRow,
  CoverageRoleKind,
  CoverageSection,
  CoverageStaff
} from "../types";
import { UNASSIGNED_COVERAGE_GROUP_ID } from "../types";

export const CONFIGURATION_LIST_FIRST = 100;

function personForKind(kind: CoverageRoleKind, section: CoverageSection) {
  return kind === "recruiter" ? section.recruiter : section.playerEngagementConsultant;
}

function compareLabels(left: string, right: string) {
  return left.localeCompare(right, undefined, { sensitivity: "base" });
}

function roleSortValue(roleType?: CoverageGroupMember["roleType"]) {
  if (roleType === "CAREER_CONSULTANT") return 0;
  if (roleType === "DIRECTOR") return 1;
  return 2;
}

function sortGroups(groups: CoverageGroupRow[]): CoverageGroupRow[] {
  return [...groups].sort((left, right) => {
    if (left.isUnassigned !== right.isUnassigned) {
      return left.isUnassigned ? 1 : -1;
    }
    return compareLabels(left.label, right.label);
  });
}

function sortMembers(members: CoverageGroupMember[]): CoverageGroupMember[] {
  return [...members].sort((left, right) => {
    const bySection = compareLabels(left.sectionName, right.sectionName);
    if (bySection !== 0) {
      return bySection;
    }
    const byPerson = compareLabels(left.person?.name ?? "", right.person?.name ?? "");
    if (byPerson !== 0) {
      return byPerson;
    }
    return roleSortValue(left.roleType) - roleSortValue(right.roleType);
  });
}

export function parseCoverageGroupBy(value: unknown): CoverageGroupBy {
  return value === "section" ? "section" : "consultant";
}

export function sectionOfficeLocation(section: CoverageSection): string | null {
  const text = formatLocationText(section.sectionOffice?.mainAddress?.city, section.sectionOffice?.mainAddress?.state);
  return text || null;
}

export function formatCoveragePrimary(label: string, location?: string | null): string {
  return location ? `${label}, ${location}` : label;
}

export function uniqueCoverageLocation(locations: Array<string | null | undefined>): string | null {
  if (locations.length === 0) return null;
  const unique = [...new Set(locations.map((value) => value || ""))];
  if (unique.length !== 1) return null;
  return unique[0] || null;
}

export function coverageMembersCsv(group: CoverageGroupRow, groupBy: CoverageGroupBy): string {
  return group.members
    .map((member) => {
      const label = groupBy === "consultant" ? member.sectionName : member.person?.name;
      if (!label) return null;
      return member.roleType ? `${label} (${sectionConsultantRoleLabel(member.roleType)})` : label;
    })
    .filter((name): name is string => Boolean(name))
    .join(", ");
}

function locationBySectionId(sections: CoverageSection[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const section of sections) {
    const location = sectionOfficeLocation(section);
    if (location) {
      map.set(section.id, location);
    }
  }
  return map;
}

export function memberMatchesSearch(member: CoverageGroupMember, query: string): boolean {
  if (!query) return true;
  const haystack = [
    member.sectionName,
    member.location,
    member.person?.name,
    member.person?.email,
    member.roleType ? sectionConsultantRoleLabel(member.roleType) : null
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query.toLowerCase());
}

export function isCoverageListTruncated(
  connection:
    | {
        pageInfo?: { hasNextPage?: boolean } | null;
        totalCount?: number | null;
        nodes?: unknown[] | null;
      }
    | null
    | undefined
): boolean {
  if (!connection) return false;
  if (connection.pageInfo?.hasNextPage) return true;
  const total = connection.totalCount;
  const shown = connection.nodes?.length ?? 0;
  return typeof total === "number" && total > shown;
}

export function filterCoverageGroups(groups: CoverageGroupRow[], search: string): CoverageGroupRow[] {
  const query = search.trim();
  if (!query) return groups;
  return groups
    .map((group) => {
      if ([group.label, group.location].filter(Boolean).join(" ").toLowerCase().includes(query.toLowerCase())) {
        return group;
      }
      const members = group.members.filter((member) => memberMatchesSearch(member, query));
      if (members.length === 0) {
        return null;
      }
      return { ...group, members };
    })
    .filter((group): group is CoverageGroupRow => Boolean(group));
}

export function coverageMemberRowId(sectionId: string) {
  return `${sectionId}-member`;
}

export function careerAssignmentMemberRowId(assignmentId: string) {
  return `assignment-${assignmentId}`;
}

function assignmentMember(assignment: CareerConsultantAssignment, location?: string | null): CoverageGroupMember {
  return {
    id: careerAssignmentMemberRowId(assignment.id),
    sectionId: assignment.section.id,
    sectionName: assignment.section.name,
    person: assignment.consultant,
    roleType: assignment.roleType,
    assignmentId: assignment.id,
    location: location ?? null
  };
}

export function groupCareerAssignmentsByConsultant(
  assignments: CareerConsultantAssignment[],
  sections: CoverageSection[] = []
): CoverageGroupRow[] {
  const locations = locationBySectionId(sections);
  const byConsultant = new Map<string, CoverageGroupRow>();

  for (const assignment of assignments) {
    const existing = byConsultant.get(assignment.consultant.id);
    const member = assignmentMember(assignment, locations.get(assignment.section.id));
    if (existing) {
      existing.members.push(member);
      continue;
    }
    byConsultant.set(assignment.consultant.id, {
      id: assignment.consultant.id,
      label: assignment.consultant.name,
      isUnassigned: false,
      canAdd: true,
      members: [member],
      lockedPerson: assignment.consultant
    });
  }

  return sortGroups(
    Array.from(byConsultant.values()).map((group) => {
      const members = sortMembers(group.members);
      return {
        ...group,
        members,
        location: uniqueCoverageLocation(members.map((member) => member.location))
      };
    })
  );
}

export function groupCareerAssignmentsBySection(
  sections: CoverageSection[],
  assignments: CareerConsultantAssignment[]
): CoverageGroupRow[] {
  const membersBySection = new Map<string, CoverageGroupMember[]>();
  const locations = locationBySectionId(sections);
  for (const assignment of assignments) {
    const members = membersBySection.get(assignment.section.id) ?? [];
    members.push(assignmentMember(assignment, locations.get(assignment.section.id)));
    membersBySection.set(assignment.section.id, members);
  }

  const sectionIds = new Set<string>();
  const groups: CoverageGroupRow[] = [];

  for (const section of sections) {
    sectionIds.add(section.id);
    groups.push({
      id: section.id,
      label: section.name,
      location: sectionOfficeLocation(section),
      isUnassigned: false,
      canAdd: true,
      members: sortMembers(membersBySection.get(section.id) ?? []),
      lockedSectionId: section.id,
      lockedSectionName: section.name
    });
  }

  for (const assignment of assignments) {
    if (sectionIds.has(assignment.section.id)) {
      continue;
    }
    sectionIds.add(assignment.section.id);
    groups.push({
      id: assignment.section.id,
      label: assignment.section.name,
      location: locations.get(assignment.section.id) ?? null,
      isUnassigned: false,
      canAdd: true,
      members: sortMembers(membersBySection.get(assignment.section.id) ?? []),
      lockedSectionId: assignment.section.id,
      lockedSectionName: assignment.section.name
    });
  }

  return sortGroups(groups);
}

function coverageMember(section: CoverageSection, person: CoverageStaff | null): CoverageGroupMember {
  return {
    id: coverageMemberRowId(section.id),
    sectionId: section.id,
    sectionName: section.name,
    person,
    location: sectionOfficeLocation(section)
  };
}

export function groupCoverageByPerson(sections: CoverageSection[], kind: CoverageRoleKind): CoverageGroupRow[] {
  const byPerson = new Map<string, CoverageGroupRow>();
  const unassignedMembers: CoverageGroupMember[] = [];

  for (const section of sections) {
    const person = personForKind(kind, section) ?? null;
    if (!person) {
      unassignedMembers.push(coverageMember(section, null));
      continue;
    }

    const existing = byPerson.get(person.id);
    const member = coverageMember(section, person);
    if (existing) {
      existing.members.push(member);
      continue;
    }
    byPerson.set(person.id, {
      id: person.id,
      label: person.name,
      isUnassigned: false,
      canAdd: true,
      members: [member],
      lockedPerson: person
    });
  }

  const groups = Array.from(byPerson.values()).map((group) => {
    const members = sortMembers(group.members);
    return {
      ...group,
      members,
      location: uniqueCoverageLocation(members.map((member) => member.location))
    };
  });

  if (unassignedMembers.length > 0) {
    groups.push({
      id: UNASSIGNED_COVERAGE_GROUP_ID,
      label: "Unassigned",
      location: null,
      isUnassigned: true,
      canAdd: false,
      members: sortMembers(unassignedMembers)
    });
  }

  return sortGroups(groups);
}

export function groupCoverageBySection(sections: CoverageSection[], kind: CoverageRoleKind): CoverageGroupRow[] {
  return sortGroups(
    sections.map((section) => {
      const person = personForKind(kind, section) ?? null;
      return {
        id: section.id,
        label: section.name,
        location: sectionOfficeLocation(section),
        isUnassigned: !person,
        canAdd: true,
        members: person ? [coverageMember(section, person)] : [],
        lockedSectionId: section.id,
        lockedSectionName: section.name
      };
    })
  );
}
